import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import {
  LOGIST_WEIGHT_CHECK_LABELS,
  LOGIST_METROLOGY_LABELS,
  LOGIST_WEIGHT_RISK_LABELS,
  LOGIST_WEIGHING_SOURCE_LABELS,
  type LogistTrip,
  type LogistWeighingSource,
} from '@entities/logistics';
import {
  useListLogistWeighingsQuery,
  useAddLogistWeighingMutation,
  useDeleteLogistWeighingMutation,
  useSetLogistTripPermitMutation,
  useRecheckLogistTripMutation,
} from '@shared/api';

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
`;

const SubSectionTitle = styled(SectionTitle)`
  font-size: 14px;
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 900px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  align-items: center;
`;

const SmallBtn = styled.button`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

const Badge = styled.span<{ $tone: 'ok' | 'bad' | 'muted' }>`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: #fff;
  background: ${({ theme, $tone }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'bad'
        ? theme.colors.status.danger
        : theme.colors.text.secondary};
`;

const WeighingSummary = styled.div`
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  line-height: 1.6;
`;

const ContoursBox = styled.div`
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3.5)};
  line-height: 1.8;
`;

const MetrologyNote = styled.div`
  font-size: 12px;
  opacity: 0.85;
  margin-left: ${({ theme }) => theme.spacing(2)};
`;

const KoapSpan = styled.span`
  font-size: 12px;
  opacity: 0.85;
`;

const PermitWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  max-width: 440px;
`;

const PermitInput = styled(Input)`
  max-width: 200px;
`;

const WeighingsListWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const NoWeighingsNotice = styled.div`
  opacity: 0.7;
  font-size: 13px;
`;

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

function weightTone(c: LogistTrip['weight_check']): 'ok' | 'bad' | 'muted' {
  return c === 'passed' ? 'ok' : c === 'failed' ? 'bad' : 'muted';
}

export interface LogistWeighingModalProps {
  trip: LogistTrip;
  canWrite: boolean;
  onClose?: () => void;
}

export function LogistWeighingModal({ trip, canWrite, onClose }: LogistWeighingModalProps) {
  const [wf, setWf] = useState<Record<string, string>>({});
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data: weighingsData } = useListLogistWeighingsQuery(trip.id);
  const weighings = weighingsData?.weighings ?? [];

  const [addWeighing] = useAddLogistWeighingMutation();
  const [deleteWeighing] = useDeleteLogistWeighingMutation();
  const [setPermit] = useSetLogistTripPermitMutation();
  const [recheckTrip] = useRecheckLogistTripMutation();

  function wset(k: string, v: string) {
    setWf((s) => ({ ...s, [k]: v }));
  }

  async function submitWeighing() {
    setErr(null);
    setOk(null);
    const net = (wf.net_mass_kg ?? '').trim();
    const gross = (wf.gross_mass_kg ?? '').trim();
    const tare = (wf.tare_mass_kg ?? '').trim();
    if (!net && !(gross && tare)) {
      setErr('Укажите нетто либо брутто и тару.');
      return;
    }
    const body: Record<string, unknown> = {};
    for (const [key, raw] of [
      ['net_mass_kg', net],
      ['gross_mass_kg', gross],
      ['tare_mass_kg', tare],
    ] as const) {
      if (raw) {
        const n = Number(raw);
        if (Number.isNaN(n)) {
          setErr('Массы — числа.');
          return;
        }
        body[key] = n;
      }
    }
    if (wf.source) body.source = wf.source;
    if ((wf.note ?? '').trim()) body.note = (wf.note ?? '').trim();

    if ((wf.scale_number ?? '').trim()) body.scale_number = (wf.scale_number ?? '').trim();
    if (wf.scale_verified_until) body.scale_verified_until = wf.scale_verified_until;
    const errPct = (wf.scale_error_pct ?? '').trim();
    if (errPct) {
      const n = Number(errPct);
      if (Number.isNaN(n)) {
        setErr('Погрешность весов — число.');
        return;
      }
      body.scale_error_pct = n;
    }

    const axleText = (wf.axle_loads_text ?? '').trim();
    if (axleText) {
      const parts = axleText
        .split(/[,;\s]+/)
        .filter(Boolean)
        .map(Number);
      if (parts.some(Number.isNaN)) {
        setErr('Нагрузки по осям — числа через запятую.');
        return;
      }
      body.axle_loads_kg = parts;
    }
    try {
      await addWeighing({ tripId: trip.id, body }).unwrap();
      setWf((s) => ({ source: s.source ?? '', permit: s.permit ?? '' }));
      setOk('Взвешивание записано, вердикт пересчитан.');
    } catch (e) {
      setErr(`Не удалось: ${apiErr(e)}`);
    }
  }

  async function savePermit() {
    setErr(null);
    setOk(null);
    const raw = (wf.permit ?? '').trim();
    const val = raw ? Number(raw) : null;
    if (raw && Number.isNaN(val as number)) {
      setErr('Лимит — число.');
      return;
    }
    try {
      await setPermit({ id: trip.id, max_permitted_mass_kg: val }).unwrap();
      setOk('Лимит обновлён, вердикт пересчитан.');
    } catch (e) {
      setErr(`Не удалось: ${apiErr(e)}`);
    }
  }

  return (
    <Section>
      <SectionTitle>
        Весовой контроль · рейс № {trip.seq_no ?? '—'}{' '}
        <Badge $tone={weightTone(trip.weight_check)}>
          {LOGIST_WEIGHT_CHECK_LABELS[trip.weight_check]}
        </Badge>
      </SectionTitle>
      <Card>
        <WeighingSummary>
          План: {trip.planned_mass_kg ?? '—'} кг · Факт: {trip.actual_mass_kg ?? '—'} кг
          {trip.max_permitted_mass_kg != null && (
            <> · лимит (разрешение): {trip.max_permitted_mass_kg} кг</>
          )}
          {trip.weight_check === 'failed' && trip.overload_kg != null && (
            <>
              {' · '}
              <b>перегруз +{trip.overload_kg} кг</b>
            </>
          )}
        </WeighingSummary>

        {/* Три контура §8.4 разведены намеренно */}
        <ContoursBox>
          <div>
            <b>Можно ли выпускать:</b>{' '}
            <Badge $tone={weightTone(trip.weight_check)}>
              {LOGIST_WEIGHT_CHECK_LABELS[trip.weight_check]}
            </Badge>
            {trip.weight_axle === 'failed' && (
              <>
                {' '}
                <Badge $tone="bad">
                  перегруз оси
                  {trip.weight_details?.axle_worst_index
                    ? ` № ${trip.weight_details.axle_worst_index}`
                    : ''}
                  {trip.axle_overload_kg != null
                    ? ` +${Math.round(trip.axle_overload_kg)} кг`
                    : ''}
                </Badge>
              </>
            )}
          </div>
          <div>
            <b>Можно ли верить измерению:</b>{' '}
            <Badge
              $tone={
                trip.weight_metrology === 'ok'
                  ? 'ok'
                  : trip.weight_metrology === 'not_checked'
                    ? 'muted'
                    : 'bad'
              }
            >
              {LOGIST_METROLOGY_LABELS[trip.weight_metrology]}
            </Badge>
            {(trip.weight_details?.metrology_notes ?? []).map((n) => (
              <MetrologyNote key={n}>— {n}</MetrologyNote>
            ))}
          </div>
          <div>
            <b>Чем грозит:</b>{' '}
            <Badge $tone={trip.weight_risk === 'none' ? 'muted' : 'bad'}>
              {LOGIST_WEIGHT_RISK_LABELS[trip.weight_risk]}
            </Badge>
            {trip.weight_koap_part != null && (
              <KoapSpan> · ст. 12.21.1 КоАП, часть {trip.weight_koap_part}</KoapSpan>
            )}
          </div>
        </ContoursBox>

        {canWrite && (
          <>
            <Grid>
              <Field>
                <Label>Брутто, кг</Label>
                <Input
                  type="number"
                  value={wf.gross_mass_kg ?? ''}
                  onChange={(e) => wset('gross_mass_kg', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Тара, кг</Label>
                <Input
                  type="number"
                  value={wf.tare_mass_kg ?? ''}
                  onChange={(e) => wset('tare_mass_kg', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Нетто, кг</Label>
                <Input
                  type="number"
                  value={wf.net_mass_kg ?? ''}
                  onChange={(e) => wset('net_mass_kg', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Источник</Label>
                <Select value={wf.source ?? ''} onChange={(e) => wset('source', e.target.value)}>
                  <option value="">— вручную —</option>
                  {(Object.keys(LOGIST_WEIGHING_SOURCE_LABELS) as LogistWeighingSource[]).map(
                    (s) => (
                      <option key={s} value={s}>
                        {LOGIST_WEIGHING_SOURCE_LABELS[s]}
                      </option>
                    ),
                  )}
                </Select>
              </Field>
              <Field>
                <Label>Весы (номер)</Label>
                <Input
                  value={wf.scale_number ?? ''}
                  onChange={(e) => wset('scale_number', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Поверка весов до</Label>
                <Input
                  type="date"
                  value={wf.scale_verified_until ?? ''}
                  onChange={(e) => wset('scale_verified_until', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Погрешность весов, %</Label>
                <Input
                  type="number"
                  value={wf.scale_error_pct ?? ''}
                  onChange={(e) => wset('scale_error_pct', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Нагрузки по осям, кг</Label>
                <Input
                  placeholder="7000, 9900, 8500"
                  value={wf.axle_loads_text ?? ''}
                  onChange={(e) => wset('axle_loads_text', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Примечание</Label>
                <Input value={wf.note ?? ''} onChange={(e) => wset('note', e.target.value)} />
              </Field>
            </Grid>
            <Row>
              <Button onClick={submitWeighing}>Записать взвешивание</Button>
              <SmallBtn
                onClick={() =>
                  recheckTrip(trip.id)
                    .unwrap()
                    .catch((x) => setErr(`Не удалось: ${apiErr(x)}`))
                }
              >
                Пересчитать вердикт
              </SmallBtn>
              {onClose && (
                <SmallBtn onClick={onClose}>Скрыть панель</SmallBtn>
              )}
            </Row>
            <PermitWrapper>
              <Label>Лимит по спецразрешению, кг (перекрывает грузоподъёмность ТС)</Label>
              <Row>
                <PermitInput
                  type="number"
                  value={wf.permit ?? ''}
                  onChange={(e) => wset('permit', e.target.value)}
                  placeholder={trip.max_permitted_mass_kg?.toString() ?? 'не задан'}
                />
                <SmallBtn onClick={savePermit}>Сохранить лимит</SmallBtn>
              </Row>
            </PermitWrapper>
            {ok && <Msg>{ok}</Msg>}
            {err && <Msg $error>{err}</Msg>}
          </>
        )}

        <WeighingsListWrapper>
          <SubSectionTitle>Записи взвешивания</SubSectionTitle>
          {weighings.length === 0 ? (
            <NoWeighingsNotice>Взвешиваний пока нет.</NoWeighingsNotice>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Время</Th>
                  <Th>Брутто</Th>
                  <Th>Тара</Th>
                  <Th>Нетто</Th>
                  <Th>Источник</Th>
                  <Th>Примечание</Th>
                  {canWrite && <Th>Действия</Th>}
                </tr>
              </thead>
              <tbody>
                {weighings.map((w) => (
                  <tr key={w.id}>
                    <Td>{new Date(w.weighed_at).toLocaleString('ru-RU')}</Td>
                    <Td>{w.gross_mass_kg ?? '—'}</Td>
                    <Td>{w.tare_mass_kg ?? '—'}</Td>
                    <Td>
                      <b>{w.net_mass_kg}</b>
                    </Td>
                    <Td>{LOGIST_WEIGHING_SOURCE_LABELS[w.source]}</Td>
                    <Td>{w.note ?? '—'}</Td>
                    {canWrite && (
                      <Td>
                        <SmallBtn
                          onClick={() =>
                            deleteWeighing({ tripId: trip.id, weighingId: w.id })
                              .unwrap()
                              .catch((x) => setErr(`Не удалось: ${apiErr(x)}`))
                          }
                        >
                          Удалить
                        </SmallBtn>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </WeighingsListWrapper>
      </Card>
    </Section>
  );
}
